import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AtUri } from '@atproto/api'
import { useTabs } from '../store/tabs'
import { useBulk } from '../store/bulk'
import {
  buildCorrelationInput,
  MAX_CORRELATION_HANDLES,
  runCorrelation,
  type CorrelationInput,
  type CorrelationVerdict,
  type HopSession,
  type PairOverlap,
} from '../utils/correlateAi'
import type { AiProvider } from '../utils/aiNames'

const PROVIDERS: { key: AiProvider; label: string }[] = [
  { key: 'openai', label: 'OpenAI' },
  { key: 'anthropic', label: 'Anthropic' },
  { key: 'gemini', label: 'Gemini' },
  { key: 'xai', label: 'xAI Grok' },
]

function pctClass(n: number) {
  if (n >= 0.8) return 'bg-rose-500/20 text-rose-100 border-rose-400/40'
  if (n >= 0.55) return 'bg-amber-400/20 text-amber-100 border-amber-400/40'
  if (n >= 0.3) return 'bg-yellow-400/15 text-yellow-100 border-yellow-400/30'
  return 'bg-emerald-500/15 text-emerald-100 border-emerald-400/30'
}

function jaccardClass(n: number) {
  if (n === 0) return 'bg-rose-500/20 text-rose-100 border-rose-400/40'
  if (n < 0.02) return 'bg-amber-400/20 text-amber-100 border-amber-400/40'
  if (n < 0.1) return 'bg-yellow-400/15 text-yellow-100 border-yellow-400/30'
  return 'bg-emerald-500/15 text-emerald-100 border-emerald-400/30'
}

function fmtPct(n: number, digits = 2): string {
  return `${(n * 100).toFixed(digits)}%`
}

function suspicionScore(
  p: PairOverlap,
  a: { likedSubjectCount: number; adultShare: number; adultLabelHits: number },
  b: { likedSubjectCount: number; adultShare: number; adultLabelHits: number },
): number {
  const minLikes = Math.min(a.likedSubjectCount, b.likedSubjectCount)
  if (minLikes < 50) return 0
  const adultPresence =
    Math.min(a.adultShare, b.adultShare) >= 0.05 ||
    Math.min(a.adultLabelHits, b.adultLabelHits) >= 25
  if (!adultPresence) return 0
  const timing = Math.max(0, (p.timingCosine - 0.5) / 0.5)
  const adultAlign = 1 - Math.min(1, Math.abs(a.adultShare - b.adultShare) / 0.5)
  return Math.max(0, Math.min(1, 0.65 * timing + 0.35 * adultAlign))
}

function topLabels(counts: Record<string, number>, n = 8): { label: string; count: number }[] {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([label, count]) => ({ label, count }))
}

function bskyUriLink(uri: string): { href: string; label: string } | null {
  try {
    const at = new AtUri(uri)
    const collection = at.collection
    const rkey = at.rkey
    const did = at.hostname
    if (collection === 'app.bsky.feed.post') {
      return { href: `https://bsky.app/profile/${did}/post/${rkey}`, label: `${did.slice(0, 14)}…/${rkey}` }
    }
    return { href: `https://bsky.app/profile/${did}`, label: `${did.slice(0, 14)}…/${collection}/${rkey}` }
  } catch {
    return null
  }
}

const HANDLE_PALETTE = [
  'hsl(340 82% 58%)',
  'hsl(200 88% 58%)',
  'hsl(150 70% 50%)',
  'hsl(40 92% 58%)',
  'hsl(280 70% 60%)',
  'hsl(20 88% 58%)',
  'hsl(180 70% 50%)',
  'hsl(310 60% 55%)',
  'hsl(70 70% 50%)',
  'hsl(230 80% 60%)',
]

function colorFor(handle: string, cluster: string[]): string {
  const idx = cluster.indexOf(handle)
  if (idx < 0) return 'hsl(0 0% 50%)'
  return HANDLE_PALETTE[idx % HANDLE_PALETTE.length]
}

export function Correlate() {
  const navigate = useNavigate()
  const tabs = useTabs((s) => s.tabs)
  const bulk = useBulk()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [input, setInput] = useState<CorrelationInput | null>(null)
  const [verdict, setVerdict] = useState<CorrelationVerdict | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const cappedTabs = useMemo(() => tabs.slice(0, MAX_CORRELATION_HANDLES), [tabs])
  const overflow = tabs.length - cappedTabs.length
  const eligible = cappedTabs.length >= 2

  function togglePair(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function previewOnly() {
    setBusy(true)
    setError(null)
    setVerdict(null)
    try {
      const r = await buildCorrelationInput(cappedTabs)
      setInput(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function runAi() {
    setBusy(true)
    setError(null)
    try {
      const r = await runCorrelation({
        handles: cappedTabs,
        provider: bulk.provider,
        apiKey: bulk.apiKey,
        model: bulk.model || undefined,
      })
      setInput(r.input)
      setVerdict(r.verdict)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const lowOverlapCandidates = useMemo(() => {
    if (!input) return []
    const out: { pair: PairOverlap; score: number }[] = []
    for (const p of input.pairwise) {
      const a = input.handles[p.i]
      const b = input.handles[p.j]
      const score = suspicionScore(p, a, b)
      if (score > 0.15) out.push({ pair: p, score })
    }
    return out.sort((x, y) => y.score - x.score)
  }, [input])

  const sortedVerdictPairs = useMemo(() => {
    if (!verdict) return []
    return [...verdict.pairs].sort((a, b) => b.likelihoodSamePerson - a.likelihoodSamePerson)
  }, [verdict])

  return (
    <main className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      <div className="glass p-5">
        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="text-xl font-extrabold tracking-tight">AI correlation across open tabs</h1>
          <button type="button" className="glass-button text-xs" onClick={() => navigate('/')}>
            ✕ close
          </button>
        </div>
        <p className="mb-4 text-sm text-ink-200">
          Compares up to {MAX_CORRELATION_HANDLES} open profiles. The model says: an
          operator who runs multiple accounts gives each account its own purpose. So we
          look for <strong>adult-content presence</strong> (porn / sexual / nudity /
          graphic-media labels) across multiple accounts that share a{' '}
          <strong>matching timing fingerprint</strong> (when each account is active). Tags
          and themes can differ — that's expected. The system picks the candidate cluster,
          ranks each member by how central they are to the operator's group, and the AI
          gives a holistic per-account "core / peripheral / unrelated" verdict.
          <br />
          <span className="text-[11px] text-ink-400">
            All inputs are local-cache only. The AI key you set on{' '}
            <Link className="accent-text underline" to="/bulk">/bulk</Link> is reused. Output
            is the model's opinion — verify before acting on it.
          </span>
        </p>

        <div className="mb-4 grid gap-2 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            provider
            <select
              className="glass-input"
              value={bulk.provider}
              onChange={(e) => bulk.setProvider(e.target.value as AiProvider)}
            >
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            api key
            <input
              type="password"
              className="glass-input"
              value={bulk.apiKey}
              onChange={(e) => bulk.setApiKey(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            model override (optional)
            <input
              className="glass-input"
              value={bulk.model}
              onChange={(e) => bulk.setModel(e.target.value)}
            />
          </label>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-ink-300">
          <strong className="text-white">{cappedTabs.length}</strong> handle
          {cappedTabs.length === 1 ? '' : 's'}:{' '}
          {cappedTabs.map((h) => (
            <span key={h} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
              @{h}
            </span>
          ))}
          {overflow > 0 ? (
            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-amber-100">
              +{overflow} extra tab{overflow === 1 ? '' : 's'} ignored (max{' '}
              {MAX_CORRELATION_HANDLES})
            </span>
          ) : null}
          {!eligible ? (
            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-amber-100">
              open at least two profiles in tabs first
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="glass-button"
            onClick={previewOnly}
            disabled={!eligible || busy}
          >
            {busy && !verdict ? 'gathering…' : 'preview features'}
          </button>
          <button
            type="button"
            className="glass-button glass-button--accent"
            onClick={runAi}
            disabled={!eligible || busy || !bulk.apiKey}
          >
            {busy ? 'asking AI…' : 'run AI correlation'}
          </button>
        </div>

        {error ? (
          <div className="mt-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
            {error}
          </div>
        ) : null}
      </div>

      {input?.hop ? (
        <div className="glass-tinted mt-3 p-4 text-sm">
          <h2 className="mb-2 text-lg font-bold">
            Who is most likely the same person
            <span className="ml-2 text-xs font-normal text-ink-300">
              ({verdict?.groupRanking?.length ? 'AI-ranked' : 'centrality-ranked'} — top of the cluster)
            </span>
          </h2>
          {verdict?.groupRanking?.length ? (
            <ol className="space-y-2">
              {verdict.groupRanking
                .slice()
                .sort((a, b) => b.score - a.score)
                .map((m, i) => (
                  <li
                    key={m.handle}
                    className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs"
                  >
                    <div className="mb-1 flex flex-wrap items-baseline gap-2">
                      <span className="font-semibold">
                        {i + 1}. @{m.handle}
                      </span>
                      <span
                        className={`rounded-full border px-2 py-0.5 ${pctClass(m.score)}`}
                      >
                        {(m.score * 100).toFixed(0)}% · {m.bucket}
                      </span>
                    </div>
                    <p className="text-ink-200">{m.reason}</p>
                  </li>
                ))}
            </ol>
          ) : (
            <ol className="space-y-2">
              {input.hop.ranking.map((m, i) => (
                <li
                  key={m.handle}
                  className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs"
                >
                  <div className="mb-1 flex flex-wrap items-baseline gap-2">
                    <span className="font-semibold">
                      {i + 1}. @{m.handle}
                    </span>
                    <span
                      className={`rounded-full border px-2 py-0.5 ${pctClass(m.centrality)}`}
                    >
                      {(m.centrality * 100).toFixed(0)}% centrality ·{' '}
                      {m.isCore ? 'core' : 'peripheral'}
                    </span>
                  </div>
                  <p className="text-ink-200">{m.themePurpose}</p>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-2 text-[11px] text-ink-400">
            Centrality is the average pair-operator-score (timing match + adult-share
            alignment) of each cluster member against the rest. The AI ranking, when
            available, replaces centrality with a holistic per-account verdict.
          </p>
        </div>
      ) : null}

      {verdict ? (
        <div className="glass-tinted mt-3 p-4 text-sm">
          <h2 className="mb-2 text-lg font-bold">
            AI verdict — pairs
            <span className="ml-2 text-xs font-normal text-ink-300">
              (sorted — most-likely same person at the top)
            </span>
          </h2>
          <div className="space-y-3">
            {sortedVerdictPairs.map((p, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="mb-1 flex flex-wrap items-baseline gap-3">
                  <span className="font-semibold">@{p.a} ↔ @{p.b}</span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs ${pctClass(p.likelihoodSamePerson)}`}
                  >
                    {(p.likelihoodSamePerson * 100).toFixed(0)}% likely same person ·{' '}
                    {p.confidence}
                  </span>
                </div>
                <p className="text-xs text-ink-200">{p.reasoning}</p>
                {p.signals?.length ? (
                  <ul className="mt-2 list-disc pl-5 text-[11px] text-ink-300">
                    {p.signals.map((s, k) => (
                      <li key={k}>{s}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
          {verdict.hopOrder ? (
            <div className="mt-4 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 p-3 text-xs">
              <div className="mb-1 flex items-baseline gap-2">
                <strong className="text-sm">hop-order narrative:</strong>
                <span className="rounded-full border border-white/10 bg-black/30 px-2 py-0.5 font-mono">
                  {verdict.hopOrder.cluster.map((h) => `@${h}`).join(' · ')}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 ${pctClass(verdict.hopOrder.confidence)}`}
                >
                  {(verdict.hopOrder.confidence * 100).toFixed(0)}% confidence
                </span>
              </div>
              <p className="text-ink-200">{verdict.hopOrder.narrative}</p>
            </div>
          ) : null}
          {verdict.groupVerdict ? (
            <div className="mt-3 rounded-xl border border-violet-400/30 bg-violet-500/10 p-3 text-xs">
              <div className="mb-1 flex items-baseline gap-2">
                <strong className="text-sm">all-{cappedTabs.length} verdict:</strong>
                <span
                  className={`rounded-full border px-2 py-0.5 text-xs ${pctClass(verdict.groupVerdict.likelihoodAllSame)}`}
                >
                  {(verdict.groupVerdict.likelihoodAllSame * 100).toFixed(0)}% likely all
                  same
                </span>
              </div>
              <p className="text-ink-200">{verdict.groupVerdict.reasoning}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {input?.hop ? (
        <div className="glass mt-3 p-4 text-sm">
          <h2 className="mb-2 text-base font-bold">
            Operator hop timeline
            <span className="ml-2 text-xs font-normal text-ink-300">
              (cluster: {input.hop.cluster.map((h) => `@${h}`).join(' · ')})
            </span>
          </h2>
          <div className="mb-2 flex flex-wrap items-baseline gap-3 text-xs text-ink-300">
            <span>
              <strong className="text-white">{input.hop.switches.toLocaleString()}</strong>{' '}
              switches
            </span>
            <span>
              avg gap{' '}
              <strong className="text-white">
                {(input.hop.avgGapBetweenSwitchesMs / 1000).toFixed(1)}s
              </strong>{' '}
              between switches
            </span>
            <span>
              median session{' '}
              <strong className="text-white">{input.hop.medianSessionActions}</strong>{' '}
              actions
            </span>
            <span>
              {input.hop.sessions.length.toLocaleString()} sessions total
            </span>
          </div>
          <HopStrip sessions={input.hop.sessions} cluster={input.hop.cluster} />
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-ink-300">
              hop sequence (collapsed runs of ≥4 shown as @h ×N)
            </summary>
            <p className="mt-1 break-all rounded-xl border border-white/10 bg-black/30 p-2 font-mono text-[11px] text-ink-200">
              {input.hop.hopSequence}
            </p>
          </details>
        </div>
      ) : null}

      {input ? (
        <>
          {lowOverlapCandidates.length > 0 ? (
            <div className="glass mt-3 p-4 text-sm">
              <h2 className="mb-2 text-base font-bold">
                Same-operator pair candidates
                <span className="ml-2 text-xs font-normal text-ink-300">
                  (adult-content presence on both + matching timing fingerprint)
                </span>
              </h2>
              <div className="space-y-1.5">
                {lowOverlapCandidates.map(({ pair, score }, k) => {
                  const a = input.handles[pair.i]
                  const b = input.handles[pair.j]
                  return (
                    <div
                      key={k}
                      className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs"
                    >
                      <div>
                        <span className="font-semibold">@{a.handle} ↔ @{b.handle}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-ink-300">
                        <span>timing {fmtPct(pair.timingCosine, 0)}</span>
                        <span>
                          adult {fmtPct(a.adultShare, 0)} / {fmtPct(b.adultShare, 0)}
                        </span>
                        <span>likeJ {fmtPct(pair.likeJaccard)}</span>
                        <span
                          className={`rounded-full border px-2 py-0.5 ${pctClass(score)}`}
                          title="local heuristic — final call is from the AI"
                        >
                          score {Math.round(score * 100)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}

          <div className="glass mt-3 p-4 text-sm">
            <h2 className="mb-2 text-base font-bold">
              Pair evidence
              <span className="ml-2 text-xs font-normal text-ink-300">
                (click to expand and see the actual shared posts / threads / labels)
              </span>
            </h2>
            <div className="space-y-2">
              {input.pairwise.map((p) => {
                const a = input.handles[p.i]
                const b = input.handles[p.j]
                const key = `${p.i}-${p.j}`
                const isOpen = expanded.has(key)
                return (
                  <div
                    key={key}
                    className="rounded-xl border border-white/10 bg-black/20"
                  >
                    <button
                      type="button"
                      onClick={() => togglePair(key)}
                      className="flex w-full flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-left text-xs"
                    >
                      <span className="font-semibold">
                        {isOpen ? '▾' : '▸'} @{a.handle} ↔ @{b.handle}
                      </span>
                      <span className="flex flex-wrap gap-2 text-ink-300">
                        <span title="hour-of-day similarity">
                          timing {fmtPct(p.timingCosine, 0)}
                        </span>
                        <span title="hashtag vector similarity">
                          tags {fmtPct(p.tagCosine, 0)}
                        </span>
                        <span title="liked-label vector similarity">
                          tasteCos {fmtPct(p.labelCosine, 0)}
                        </span>
                        <span>{p.likeOverlapCount} shared likes</span>
                        <span>{p.replyOverlapCount} shared threads</span>
                      </span>
                    </button>
                    {isOpen ? (
                      <PairEvidence
                        pair={p}
                        handleA={a.handle}
                        handleB={b.handle}
                        labelsA={a.likedLabelCounts}
                        labelsB={b.likedLabelCounts}
                      />
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="glass mt-3 p-4 text-sm">
            <h2 className="mb-2 text-base font-bold">
              Like overlap matrix
              <span className="ml-2 text-xs font-normal text-ink-300">
                (Jaccard of liked subject URIs)
              </span>
            </h2>
            <OverlapTable
              handles={input.handles.map((h) => h.handle)}
              pairs={input.pairwise}
              field="likeJaccard"
              counts={input.handles.map((h) => h.likedSubjectCount)}
            />
          </div>

          <div className="glass mt-3 p-4 text-sm">
            <h2 className="mb-2 text-base font-bold">
              Reply-parent overlap matrix
              <span className="ml-2 text-xs font-normal text-ink-300">
                (Jaccard of distinct threads they replied into)
              </span>
            </h2>
            <OverlapTable
              handles={input.handles.map((h) => h.handle)}
              pairs={input.pairwise}
              field="replyJaccard"
              counts={input.handles.map((h) => h.replyParentCount)}
            />
          </div>

          <div className="glass mt-3 p-4 text-sm">
            <h2 className="mb-2 text-base font-bold">Content tag profile (top labels in liked content)</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {input.handles.map((h) => {
                const tops = topLabels(h.likedLabelCounts, 8)
                const totalLabels = Object.values(h.likedLabelCounts).reduce(
                  (s, n) => s + n,
                  0,
                )
                return (
                  <div
                    key={h.handle}
                    className="rounded-xl border border-white/10 bg-black/20 p-3"
                  >
                    <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                      <strong className="text-sm">@{h.handle}</strong>
                      <span className="text-[10px] text-ink-400">
                        {h.likedImageCount} image · {h.likedVideoCount} video ·{' '}
                        {h.contentProfileSamples} sampled
                      </span>
                    </div>
                    <div className="mb-1.5 flex flex-wrap items-baseline gap-2 text-[11px]">
                      <span
                        className={`rounded-full border px-2 py-0.5 ${pctClass(h.adultShare)}`}
                        title={`${h.adultLabelHits} adult-label hits across cached liked content`}
                      >
                        adult {(h.adultShare * 100).toFixed(0)}% (
                        {h.adultLabelHits.toLocaleString()})
                      </span>
                      {h.dominantLabel ? (
                        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-ink-200">
                          purpose: {h.dominantLabel} ·{' '}
                          {(h.dominantLabelShare * 100).toFixed(0)}%
                        </span>
                      ) : null}
                    </div>
                    {tops.length > 0 ? (
                      <ul className="space-y-1 text-xs">
                        {tops.map(({ label, count }) => {
                          const pct = totalLabels === 0 ? 0 : count / totalLabels
                          return (
                            <li key={label} className="flex items-center gap-2">
                              <span className="w-32 shrink-0 truncate font-mono text-[11px]">
                                {label}
                              </span>
                              <span
                                className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5"
                                title={`${count}`}
                              >
                                <span
                                  className="block h-full bg-gradient-to-r from-fuchsia-500/60 to-rose-500/60"
                                  style={{ width: `${Math.max(2, pct * 100)}%` }}
                                />
                              </span>
                              <span className="w-10 shrink-0 text-right text-[10px] text-ink-300">
                                {count}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    ) : (
                      <p className="text-xs text-ink-400">
                        no labelled liked media in cache (run ⤓ pull-everything on this
                        handle to populate it)
                      </p>
                    )}
                    {h.likedHashtags.length > 0 ? (
                      <p className="mt-2 text-[11px] text-ink-300">
                        liked hashtags:{' '}
                        {h.likedHashtags.slice(0, 8).map((t) => (
                          <span
                            key={t.tag}
                            className="mr-1 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5"
                          >
                            #{t.tag}
                            <span className="ml-1 text-ink-400">{t.count}</span>
                          </span>
                        ))}
                      </p>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : null}

      {input ? (
        <details className="glass mt-3 p-4 text-xs">
          <summary className="cursor-pointer text-sm font-bold">
            raw features (what the AI saw)
          </summary>
          <pre className="mt-2 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all text-[10px] text-ink-300">
            {JSON.stringify(input, null, 2)}
          </pre>
        </details>
      ) : null}
    </main>
  )
}

function PairEvidence({
  pair,
  handleA,
  handleB,
  labelsA,
  labelsB,
}: {
  pair: PairOverlap
  handleA: string
  handleB: string
  labelsA: Record<string, number>
  labelsB: Record<string, number>
}) {
  const sharedLabels = useMemo(() => {
    const out: { label: string; a: number; b: number }[] = []
    for (const [k, v] of Object.entries(labelsA)) {
      const w = labelsB[k]
      if (w) out.push({ label: k, a: v, b: w })
    }
    return out.sort((x, y) => y.a + y.b - (x.a + x.b))
  }, [labelsA, labelsB])

  return (
    <div className="space-y-3 border-t border-white/10 px-3 py-3 text-xs">
      <UriList
        title={`Posts both @${handleA} and @${handleB} ❤️'d (${pair.likeOverlapCount} total, showing ${pair.sharedLikedSubjectSample.length})`}
        emptyHint="no shared liked posts in cache — that's the suspicious case if both have substantial likes"
        uris={pair.sharedLikedSubjectSample}
      />
      <UriList
        title={`Threads both replied to (${pair.replyOverlapCount} total, showing ${pair.sharedReplyParentSample.length})`}
        emptyHint="no shared reply threads"
        uris={pair.sharedReplyParentSample}
      />
      <div>
        <div className="mb-1 font-semibold text-white">Shared content labels</div>
        {sharedLabels.length === 0 ? (
          <p className="text-ink-400">
            no labels in common (taste profiles diverge, or hydration hasn't run for one of
            them)
          </p>
        ) : (
          <table className="min-w-full border-collapse text-[11px]">
            <thead>
              <tr>
                <th className="border-b border-white/10 p-1 text-left text-ink-400">label</th>
                <th className="border-b border-white/10 p-1 text-right text-ink-400">
                  @{handleA}
                </th>
                <th className="border-b border-white/10 p-1 text-right text-ink-400">
                  @{handleB}
                </th>
              </tr>
            </thead>
            <tbody>
              {sharedLabels.slice(0, 12).map((l) => (
                <tr key={l.label}>
                  <td className="p-1 font-mono">{l.label}</td>
                  <td className="p-1 text-right">{l.a}</td>
                  <td className="p-1 text-right">{l.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="text-[10px] text-ink-400">
        timing: {pair.secondOverlap.toLocaleString()} second-overlap ·{' '}
        {pair.msOverlap.toLocaleString()} ms-overlap (millisecond-precise)
      </div>
    </div>
  )
}

function UriList({
  title,
  emptyHint,
  uris,
}: {
  title: string
  emptyHint: string
  uris: string[]
}) {
  return (
    <div>
      <div className="mb-1 font-semibold text-white">{title}</div>
      {uris.length === 0 ? (
        <p className="text-ink-400">{emptyHint}</p>
      ) : (
        <ul className="grid gap-0.5 sm:grid-cols-2">
          {uris.map((u) => {
            const link = bskyUriLink(u)
            if (!link) {
              return (
                <li key={u} className="truncate font-mono text-[10px] text-ink-300">
                  {u}
                </li>
              )
            }
            return (
              <li key={u} className="truncate text-[11px]">
                <a
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="accent-text hover:underline"
                >
                  {link.label} ↗
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function HopStrip({
  sessions,
  cluster,
}: {
  sessions: HopSession[]
  cluster: string[]
}) {
  // Map sessions onto a horizontal strip of segments proportional to count
  // (action volume, NOT real-time duration — real time can have huge gaps,
  // but action density is what tells the hop story).
  const total = sessions.reduce((s, x) => s + x.count, 0)
  if (total === 0) return null
  const minRender = Math.max(1, total * 0.001)
  return (
    <div>
      <div className="mb-1 flex flex-wrap gap-2 text-[10px]">
        {cluster.map((h) => (
          <span key={h} className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: colorFor(h, cluster) }}
            />
            @{h}
          </span>
        ))}
      </div>
      <div className="flex h-5 w-full overflow-hidden rounded-md border border-white/10 bg-black/30">
        {sessions.map((s, i) => {
          const w = (Math.max(s.count, minRender) / total) * 100
          return (
            <span
              key={i}
              className="h-full"
              style={{
                width: `${w}%`,
                background: colorFor(s.handle, cluster),
                opacity: 0.85,
              }}
              title={`@${s.handle} · ${s.count} actions · ${s.startTs} → ${s.endTs}`}
            />
          )
        })}
      </div>
    </div>
  )
}

function OverlapTable({
  handles,
  pairs,
  field,
  counts,
}: {
  handles: string[]
  pairs: PairOverlap[]
  field: 'likeJaccard' | 'replyJaccard'
  counts: number[]
}) {
  const matrix = new Map<string, number>()
  for (const p of pairs) {
    matrix.set(`${p.i}-${p.j}`, p[field])
    matrix.set(`${p.j}-${p.i}`, p[field])
  }
  return (
    <div className="overflow-auto">
      <table className="min-w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="border border-white/10 bg-white/5 p-1.5 text-left text-[10px] uppercase tracking-widest text-ink-300">
              handle
            </th>
            {handles.map((h, i) => (
              <th
                key={h}
                className="border border-white/10 bg-white/5 p-1.5 text-[10px] font-mono"
                title={`${counts[i]} entries`}
              >
                @{h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {handles.map((rowHandle, i) => (
            <tr key={rowHandle}>
              <td className="border border-white/10 bg-white/5 p-1.5 text-[10px] font-mono">
                @{rowHandle}
                <span className="ml-1 text-ink-400">({counts[i]})</span>
              </td>
              {handles.map((_, j) => {
                if (i === j) {
                  return (
                    <td
                      key={j}
                      className="border border-white/10 bg-white/10 p-1.5 text-center text-ink-400"
                    >
                      —
                    </td>
                  )
                }
                const v = matrix.get(`${i}-${j}`) ?? 0
                return (
                  <td
                    key={j}
                    className={`border border-white/10 p-1.5 text-center font-mono text-[11px] ${jaccardClass(v)}`}
                  >
                    {fmtPct(v)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
